import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import Portal from '../../general-components/portal';
import { setMetaMenuRoute, setMetaMenuState } from '../../../stores/actions/tabs';
import { META_MENU_ROUTES, META_MENU_STATES } from '../../../stores/types/tabs';
import { setSelectedOnsiteLocationId, setSharingScreen } from '../../../stores/actions/video';
import { GreenroomState as GS } from '../../../stores/types/types';
import { CreateOnsiteLocation } from '../../../connection/video';
import { EntityType } from '../../../types/enums';
import { STATIC_ASSET_URL } from '../../../utils/content-templates';
import logger from '../../../utils/logger-rum';

export default function AddFeedButton() {
	const dispatch = useDispatch();

	const sharingScreen = useSelector((s: GS) => s.VideoReducer.sharingScreen);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const usePixel = useSelector((s: GS) => s.SettingsReducer.featureFlags?.use_pixel);

	// option arrows render smaller without the pixel (v3) design
	const arrowSize = usePixel ? 16 : 12;

	const [optionsOpen, setOptionsOpen] = useState<boolean>(false);
	const [optionClicked, setOptionClicked] = useState<boolean>(false);

	const handleClick = () => {
		setOptionsOpen(false);
		setOptionClicked(false);
		window.removeEventListener('click', handleClick);
	};

	const handleOpenOptions = () => {
		if (!optionsOpen) {
			setOptionsOpen(true);
			setTimeout(() => {
				window.addEventListener('click', handleClick);
			}, 100);
		}
	};

	const handleInviteUser = () => {
		dispatch(setMetaMenuRoute(META_MENU_ROUTES.SHARING));
		dispatch(setMetaMenuState(META_MENU_STATES.OPENING));
	};

	const handleShareScreen = () => {
		dispatch(setSharingScreen(!sharingScreen));
	};

	const handleLocation = async () => {
		setOptionClicked(true);
		if (!token || !meeting) return;
		const entity = await CreateOnsiteLocation(
			token,
			meeting.meeting,
			null,
			`${STATIC_ASSET_URL}/default-images/onsite-safety-image.png`,
			null,
		);
		if (!entity || entity.entityType !== EntityType.onsiteLocation) {
			logger.error('Failed to create onsite location');
			return;
		}
		const locationId = Number(entity.entityId);
		if (locationId) {
			dispatch(setSelectedOnsiteLocationId(locationId));
			dispatch(setMetaMenuRoute(META_MENU_ROUTES.ONSITE_LOCATION));
			dispatch(setMetaMenuState(META_MENU_STATES.OPENING));
		}
	};

	useEffect(() => () => {
		window.removeEventListener('click', handleClick);
	}, []);

	return (
		<>
			<button type="button" className={classNames('add-feed-button', optionsOpen && 'open')} onClick={handleOpenOptions}>
				<label className="add-feed-button-label">Add</label>
				<Icon
					name={
						optionsOpen
							? ICONS.KEYBOARD_ARROW_UP
							: ICONS.KEYBOARD_ARROW_DOWN
					}
					color={COLORS.TEXT_GRAY_200}
					size={12}
				/>
			</button>
			<Portal targetId="add-feed-portal">
				<div
					className={classNames('add-feeds-container field-group select', optionsOpen ? 'open' : '')}
					onTransitionEnd={({ propertyName, currentTarget }) => {
						if (
							propertyName === 'visibility'
							&& currentTarget
							&& !currentTarget.className.includes('open')
						) {
							setOptionClicked(false);
						}
					}}
				>
					<button type="button" onClick={handleInviteUser} className='add-feed-option select-option'>
						<div className="add-feed-option-icon">
							<Icon name={ICONS.SHARE} color={COLORS.WHITE} size={16} />
						</div>
						<label className="add-feed-option-label">Invite User</label>
						<span className="add-feed-option-arrow">
							<Icon name={ICONS.KEYBOARD_ARROW_RIGHT} color={COLORS.WHITE} size={arrowSize} />
						</span>
					</button>
					<button type="button" onClick={handleShareScreen} className={classNames('add-feed-option select-option', {'critical-option': sharingScreen})}>
						<div className="add-feed-option-icon">
							<Icon
								name={sharingScreen ? ICONS.STOP_SCREENSHARE : ICONS.SHARE_SCREEN}
								color={sharingScreen ? COLORS.SEMANTIC_RED_400 : COLORS.WHITE}
								size={16}
							/>
						</div>
						<label className={classNames('add-feed-option-label', sharingScreen && 'semantic-red-400')}>
							{sharingScreen ? 'Stop Sharing Screen' : 'Share Screen'}
						</label>
						<span className="add-feed-option-arrow">
							<Icon name={ICONS.KEYBOARD_ARROW_RIGHT} color={sharingScreen ? COLORS.SEMANTIC_RED_400 : COLORS.WHITE} size={arrowSize} />
						</span>
					</button>
					<button type="button" onClick={handleLocation} disabled={optionClicked} className='add-feed-option select-option'>
						<div className="add-feed-option-icon">
							<Icon name={ICONS.LOCATION} color={COLORS.WHITE} size={16} />
						</div>
						<label className="add-feed-option-label">Location</label>
						<span className="add-feed-option-arrow">
							<Icon name={ICONS.KEYBOARD_ARROW_RIGHT} color={COLORS.WHITE} size={arrowSize} />
						</span>
					</button>
				</div>
			</Portal>
		</>
	);
}
